/**
 * TimeSlot Client-to-Display Adapter
 *
 * Transforms ClientTimeSlot into FullCalendar EventInput objects with strongly typed extendedProps
 */

import moment from "moment";
import { ClientTimeSlot, CalendarEvent, TimeslotTransformOptions } from "../types";
import notificationStore from "../../../../../stores/Notification/notificationStore";
import { mobiliTheme } from "../../../../../themes/mobiliTheme";

/**
 * LoA blocks at or above this duration are locked against drag/resize. Short
 * intra-day absences (a doctor's visit, a half-day training) stay adjustable;
 * long/multi-day absences (vacations) must be edited via the AbsenceDialog.
 */
export const DRAGGABLE_LOA_MAX_HOURS = 12;

/**
 * Transform ClientTimeSlot to FullCalendar EventInput (for non-appointment timeslots)
 */
export const adaptTimeSlotToCalendarEvent = (
  timeSlot: ClientTimeSlot,
  options: TimeslotTransformOptions = {},
  isPatientSlot: boolean = false
): CalendarEvent => {
  const { lockedAppointments = [] } = options;
  const isLocked = timeSlot.id ? lockedAppointments.includes(timeSlot.id) : false;

  // Long/multi-day absences are edited exclusively through the AbsenceDialog (and
  // deleted via the context menu). A draggable vacation block invites accidental
  // ±5-minute drags that silently PATCH the row and evict the vacation from the
  // visible calendar — the "disappearing vacation" production incident. Short
  // (< 12h) absences remain draggable for quick intra-day adjustments.
  const durationHours =
    (timeSlot.end.getTime() - timeSlot.start.getTime()) / (60 * 60 * 1000);
  const isImmovableLoA =
    timeSlot.type === "LeaveOfAbsence" && !(durationHours < DRAGGABLE_LOA_MAX_HOURS);

  const resourceId = isPatientSlot
    ? "0"
    : timeSlot.user?.id != null
    ? String(timeSlot.user.id)
    : undefined;
  if (!resourceId) {
    notificationStore.showMessage("⚠️ TimeSlot missing user/resource ID", "warning");
  }
  return {
    id: timeSlot.id,
    title: timeSlot.title || timeSlot.type,
    start: timeSlot.start.toISOString(),
    end: timeSlot.end.toISOString(),
    durationEditable: !isImmovableLoA,
    // Ensure resourceId is a string (FullCalendar requirement for consistent diffing)
    resourceId,

    editable: !isLocked && !isImmovableLoA,
    resizable: !isImmovableLoA,

    classNames: [timeSlot.type.toLowerCase(), isLocked ? "locked" : ""].filter(Boolean),

    color: timeSlot.type == "LunchBreak" ? mobiliTheme.palette.info.light : "#3788d8",

    display: options.isBackgroundEvent ? "background" : "auto",
    // All custom properties go in extendedProps
    extendedProps: {
      eventType: "timeslot" as const,
      timeSlotId: timeSlot.id,
      timeSlotType: timeSlot.type,
      loaType: timeSlot.loaType,

      user: timeSlot.user,
      patient: timeSlot.patient,
      therapy: timeSlot.therapy,

      attended: timeSlot.attended,
      isCancelled: timeSlot.isCancelled,
      hasNotes: timeSlot.hasNotes,
      rx: timeSlot.rx,

      treatmentIndex: timeSlot.treatmentIndex,
      reason: timeSlot.reason,
      wasDocumentedCount: timeSlot?.wasDocumentedCount,

      visitFee: timeSlot.visitFee,
      visitType: timeSlot.visitType,
      sublocality: timeSlot.sublocality,
      isContinuous: false,
    },
  };
};

export const adaptContinuousTimeSlotToCalendarEvent = (
  timeSlot: ClientTimeSlot,
  options: TimeslotTransformOptions = {}
): CalendarEvent => {
  const { lockedAppointments = [] } = options;
  const isLocked = timeSlot.id ? lockedAppointments.includes(timeSlot.id) : false;

  return {
    id: timeSlot.id,
    title: timeSlot.title || timeSlot.type,
    start: timeSlot.start.toISOString(),
    end: timeSlot.end.toISOString(),
    resourceId: timeSlot.user?.id != null ? String(timeSlot.user.id) : undefined,
    durationEditable: true,

    // startTime, endTime and daysOfWeek are used for recurring events in FullCalendar
    // They are essential for proper rendering of continuous appointments
    // even though we provide explicit start and end times above
    startTime: moment(timeSlot.start!).format("HH:mm:ss"),
    endTime: moment(timeSlot.end!).format("HH:mm:ss"),
    daysOfWeek: [moment(timeSlot.start!).day().toString()],

    editable: !isLocked,
    resizable: false,

    classNames: [timeSlot.type.toLowerCase(), isLocked ? "locked" : ""].filter(Boolean),

    color: timeSlot.type == "LunchBreak" ? mobiliTheme.palette.info.light : "#3788d8",

    // All custom properties go in extendedProps
    extendedProps: {
      eventType: "timeslot" as const,
      timeSlotId: timeSlot.id,
      timeSlotType: timeSlot.type,
      loaType: timeSlot.loaType,

      user: timeSlot.user,
      patient: timeSlot.patient,
      therapy: timeSlot.therapy,

      attended: timeSlot.attended,
      isCancelled: timeSlot.isCancelled,
      hasNotes: timeSlot.hasNotes,
      rx: timeSlot.rx,

      treatmentIndex: timeSlot.treatmentIndex,
      wasDocumentedCount: timeSlot.wasDocumentedCount,
      reason: timeSlot.reason,

      visitFee: timeSlot.visitFee,
      visitType: timeSlot.visitType,
      sublocality: timeSlot.sublocality,
      isContinuous: true,
    },
  };
};
